/**
 * The parameter machinery lenses and shapes share.
 *
 * A parameter is a named zod schema with a description, read by the route's query schema, the handler's typed values
 * and the client's query type alike. A lens and a shape are the same thing on the wire and in the handler; they differ
 * in what they mean for coverage (see `lens.ts` and `shape.ts`). Keeping the two as distinct types stops a spec from
 * putting one under the other.
 */

import type { z } from "zod";

export type ParamDefinition<TName extends string, TSchema extends z.ZodType> = {
  name: TName;
  schema: TSchema;
  /** What the route's documentation says of the parameter. */
  description: string;
};

/**
 * A spec's parameters of one role, keyed by name.
 */
export type ParamRecord = Record<string, ParamDefinition<string, z.ZodType>>;

/**
 * A mapped type folded to one object type, so an intersection of required and optional keys reads as one shape.
 */
type Flatten<T> = { [K in keyof T]: T[K] };

/**
 * The parameters whose schema accepts `undefined` on the wire: optional keys of the client's query.
 */
type OptionalQueryParam<R extends ParamRecord> = {
  [K in keyof R]: undefined extends z.input<R[K]["schema"]> ? K : never;
}[keyof R];

/**
 * The parameters whose schema may read to `undefined`: optional keys of the handler's values.
 */
type OptionalValueParam<R extends ParamRecord> = {
  [K in keyof R]: undefined extends z.output<R[K]["schema"]> ? K : never;
}[keyof R];

/**
 * The values a handler holds once the route's schema has read the parameters.
 *
 * A parameter whose schema may read to `undefined` is an optional key, so a value the request did not send is
 * absent, as the parsed object leaves it. A schema with a default reads to its default, so the key is required.
 */
export type ParamValues<R extends ParamRecord> = Flatten<
  { [K in Exclude<keyof R, OptionalValueParam<R>>]: z.output<R[K]["schema"]> } & {
    [K in OptionalValueParam<R>]?: z.output<R[K]["schema"]>;
  }
>;

/**
 * The values a client sends: the wire shape, before the schema reads it.
 *
 * A parameter whose schema accepts `undefined` is an optional key, so a caller can leave it out.
 */
export type ParamQuery<R extends ParamRecord> = Flatten<
  { [K in Exclude<keyof R, OptionalQueryParam<R>>]: z.input<R[K]["schema"]> } & {
    [K in OptionalQueryParam<R>]?: z.input<R[K]["schema"]>;
  }
>;

/**
 * The zod raw shape of a parameter record, for a query schema's `.extend`, each schema carrying its description.
 */
export function paramSchemas<R extends ParamRecord>(params: R): { [K in keyof R]: R[K]["schema"] } {
  const shape: Record<string, z.ZodType> = {};
  for (const [name, param] of Object.entries(params)) {
    shape[name] = param.schema.describe(param.description);
  }
  // Built by name from `params`, so it is the mapped type; the loop cannot say so.
  return shape as { [K in keyof R]: R[K]["schema"] };
}

/**
 * Refuses a record whose key and parameter name disagree, once, at declaration.
 *
 * @param log - The spec's name, for the message.
 * @param role - What the record holds, for the message.
 * @param params - The record to check.
 * @throws {Error} when a parameter sits under a key other than its name.
 */
export function assertParamNames(log: string, role: string, params: ParamRecord): void {
  for (const [key, param] of Object.entries(params)) {
    if (param.name !== key) {
      throw new Error(`${log}: the ${role} under "${key}" is named "${param.name}"; the key and the name must agree.`);
    }
  }
}

/**
 * Refuses a parameter name that another record, or the shared query, already uses, once, at declaration.
 *
 * The route's schema is one object of every parameter by name, so two parameters of one name would read as one; the
 * prologue parses each record on its own and would hand the handler both readings.
 *
 * @param log - The spec's name, for the message.
 * @param role - What `params` holds, for the message.
 * @param params - The record to check.
 * @param taken - The names already in use, each with what it is, for the message.
 * @throws {Error} when a parameter in `params` is named in `taken`.
 */
export function assertParamNamesFree(log: string, role: string, params: ParamRecord, taken: Map<string, string>): void {
  for (const name of Object.keys(params)) {
    const holder = taken.get(name);
    if (holder !== undefined) {
      throw new Error(`${log}: the ${role} "${name}" is also ${holder}; every parameter name must be unique.`);
    }
  }
}
